// src/utils/aiQuestionGenerator.js

// Import symptom-specific builders
import { buildPrompt as buildSleepPrompt } from './sleepAIGenerator';
import { buildWeightPrompt } from './weightAIGenerator';
import { buildDigestionPrompt } from './digestionAIGenerator';
import { buildImmunityPrompt } from './immunityAIGenerator';
import { buildMalePrompt } from './maleAIGenerator';
import { buildFemalePrompt } from './femaleAIGenerator';
import { buildHangoverPrompt } from './hangoverAIGenerator';

// const CLAUDE_API_URL = 'https://api.anthropic.com/v1/messages';
const API_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY;


/**
 * Generate AI-enhanced question text based on user's previous answers
 * @param {Object} question - The original question object
 * @param {Object} answers - All previous answers
 * @param {String} symptomId - The symptom type (sleep, weight, etc.)
 * @returns {Promise<Object>} - Enhanced question with AI-generated text
 */
export async function generateContextualQuestion(question, answers, symptomId = 'sleep', userName = '') {
  // If it's the first few questions, just return original
  if (!answers || Object.keys(answers).length < 2) {
    return question;
  }

  // If no API key, return original
  if (!API_KEY || API_KEY === 'your_api_key_here') {
    console.warn('No API key configured. Using original question text.');
    return question;
  }

  try {
    const prompt = buildPromptForSymptom(question, answers, symptomId, userName);

    const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
    const response = await fetch(`${API_URL}/api/generate-question`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt: prompt
      })
    });

    if (!response.ok) {
      console.error('AI API error:', response.status);
      return question;
    }

    const data = await response.json();

    if (!data || !data.content || !Array.isArray(data.content) || data.content.length === 0) {
      console.error('Invalid API response:', data);
      return question;
    }

    const aiText = data.content[0].text.trim();

    return {
      ...question,
      text: aiText,
      isAIGenerated: true
    };

  } catch (error) {
    console.error('Error generating AI question:', error);
    return question;
  }
}

/**
 * Build prompt based on symptom type
 */
function buildPromptForSymptom(question, answers, symptomId, userName) {
  switch (symptomId) {
    case 'sleep':
      return buildSleepPrompt(question, answers, userName);
    case 'weight':
      return buildWeightPrompt(question, answers, userName);
    case 'digestion':
      return buildDigestionPrompt(question, answers, userName);
    case 'immunity':
      return buildImmunityPrompt(question, answers, userName);
    case 'male':
      return buildMalePrompt(question, answers, userName);
    case 'female':
      return buildFemalePrompt(question, answers, userName);
    case 'hangover':
      return buildHangoverPrompt(question, answers, userName);
    // Add more symptoms here
    default:
      return buildSleepPrompt(question, answers, userName);
  }
}